import Papa from 'papaparse';

function downloadCSV(rows, filename) {
  const csv = Papa.unparse(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportFirmsToCSV(firms) {
  const rows = firms.map(firm => ({
    'Firm Name': firm.name,
    'Year Founded': firm.founded,
    'Total Employees': firm.totalEmployees,
    'Investment Strategies': (firm.strategies || []).join('; '),
    'Office Cities': (firm.offices || []).map(o => o.city).join('; '),
    'Office Countries': (firm.offices || []).map(o => o.country).join('; '),
    'Headquarters': (firm.offices || []).find(o => o.isPrimary)?.city || '',
    'Payout Structure': firm.payoutStructure,
    'Bonus Date': firm.bonusDate || '',
    'AUM': firm.aum,
    'Notes': firm.notes || '',
  }));
  downloadCSV(rows, 'investment-firms');
}

export function exportPipelinesToCSV(pipelines) {
  const rows = pipelines.map(p => ({
    'Search Name': p.name || '',
    'Company': p.firm_name || '',
    'Status': p.status || '',
    'Candidates': p.card_count || 0,
    'Location': p.location || '',
    'Created': p.created_at ? new Date(p.created_at).toLocaleDateString() : '',
  }));
  downloadCSV(rows, 'pipelines');
}

export function exportSearchesToCSV(searches, category) {
  const label = category === 'project' ? 'projects' : 'executive-searches';
  const rows = searches.map(s => ({
    'Name': s.name || '',
    'Company': s.firm_name || '',
    'Status': s.status || '',
    'Candidates': s.card_count || 0,
    'Location': s.location || '',
    'Created By': s.created_by_name || '',
    'Created': (s.submitted_at || s.created_at) ? new Date(s.submitted_at || s.created_at).toLocaleDateString() : '',
  }));
  downloadCSV(rows, label);
}

export function exportTagsToCSV(tags) {
  const rows = tags.map(t => ({
    'Tag Name': t.name || '',
    'Color': t.color || '',
    'Description': t.description || '',
    'Candidates': t.usage_count || 0,
    'Created By': t.created_by_name || '',
    'Created': t.created_at ? new Date(t.created_at).toLocaleDateString() : '',
  }));
  downloadCSV(rows, 'tags');
}

export function exportCandidatesToCSV(candidates, contextLabel, options = {}) {
  // includeComp defaults to true for backwards compat. Callers that
  // know the user lacks view_compensation should pass `{ includeComp:
  // false }` — that drops the comp columns from the header entirely
  // (vs leaving them with blank values, which is misleading because a
  // reader can't tell the difference between "no value" and "redacted").
  const includeComp = options.includeComp !== false;
  const rows = candidates.map(c => {
    // Tags — flatten to semicolon-separated string
    let tagNames = '';
    if (c.custom_tags && typeof c.custom_tags === 'object') {
      tagNames = Object.entries(c.custom_tags)
        .map(([id, data]) => {
          if (typeof data === 'string') return data;
          return data?.name || id;
        })
        .join('; ');
    }

    // Pipeline names
    const pipelines = Array.isArray(c.pipeline_names) ? c.pipeline_names.join('; ') : (c.pipeline_name || '');

    // Interview stages
    const stages = Array.isArray(c.client_stage_names) ? c.client_stage_names.join('; ') : '';

    // Compensation — format with dollar signs where available
    const fmtMoney = (v) => {
      if (v === null || v === undefined || v === '') return '';
      const n = Number(v);
      return isNaN(n) ? String(v) : `$${n.toLocaleString()}`;
    };

    // Notice/Non-compete — format as days
    const fmtDays = (v) => {
      if (v === null || v === undefined || v === '' || v === 0) return '';
      return `${v} days`;
    };

    const row = {
      'Name': c.name || '',
      'Email': c.email || '',
      'Phone': c.phone || '',
      'Title': c.client_current_title || c.current_title || '',
      'Company': c.client_current_company || c.current_company || '',
      'Location': c.display_location || c.client_location || c.location || '',
      'LinkedIn': c.linkedin || '',
      'Tags': tagNames,
      'Search / Pipeline': pipelines,
      'Interview Stage': stages,
      'Status': c.derived_status || c.status || '',
      'Rating': c.rating != null ? c.rating : '',
      'Placed': c.has_placed ? 'Yes' : '',
      'Interviewing': c.has_interviewing ? 'Yes' : '',
      'Referral Source': c.referral_source || '',
    };
    if (includeComp) {
      row['Current Base Salary'] = fmtMoney(c.current_base_salary);
      row['Current Total Comp'] = fmtMoney(c.current_total_comp);
      row['Expected Base Min'] = fmtMoney(c.expected_base_min);
      row['Expected Base Max'] = fmtMoney(c.expected_base_max);
      row['Expected Total'] = fmtMoney(c.expected_total);
      row['Bonus Expectations'] = fmtMoney(c.bonus_expectations);
    }
    row['Notice Period'] = fmtDays(c.notice_period);
    row['Non-Compete'] = fmtDays(c.non_compete);
    row['Open to Remote'] = c.open_to_remote ? 'Yes' : '';
    row['Remote Only'] = c.remote_only ? 'Yes' : '';
    row['Open to Relocation'] = c.open_to_relocation ? 'Yes' : '';
    row['Spoken To'] = c.spoken_to ? 'Yes' : '';
    row['Do Not Contact'] = c.do_not_contact ? 'Yes' : '';
    row['Clean Profile'] = c.is_clean ? 'Yes' : '';
    row['Date Added'] = c.sent_at ? new Date(c.sent_at).toLocaleDateString() : '';
    row['Last Updated'] = c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '';
    row['Last Note'] = c.last_note_at ? new Date(c.last_note_at).toLocaleDateString() : '';
    return row;
  });
  const filename = contextLabel ? `candidates-${contextLabel.replace(/\s+/g, '-').toLowerCase()}` : 'candidates';
  downloadCSV(rows, filename);
}

export function exportClientSearchesToCSV(searches, category) {
  const rows = searches.map(s => {
    const stages = Array.isArray(s.stage_summary)
      ? s.stage_summary.filter(st => st.count > 0).map(st => `${st.name === 'Sent' ? 'In Review' : st.name}: ${st.count}`).join(', ')
      : '';
    const total = Array.isArray(s.stage_summary)
      ? s.stage_summary.reduce((sum, st) => sum + (st.count || 0), 0)
      : 0;
    return {
      'Search Name': s.name || '',
      'Status': s.is_completed ? 'Completed' : (s.status || ''),
      'Location': s.location || '',
      'Compensation': s.compensation_range || '',
      'Candidates': total,
      'Stage Breakdown': stages,
      'Created': s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
      'Submitted': s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '',
    };
  });
  downloadCSV(rows, category || 'my-searches');
}
